import { Component, Input, Output, EventEmitter } from '@angular/core'
import { IPagination } from '@app/features/oms/models/proposal-summary.model'
import { IEnhancedPagination } from '@core/models/shared-table.model'
import { ZardDropdownMenuComponent } from '@/styles/components/dropdown/dropdown.component'
import { ZardDropdownMenuItemComponent } from '@/styles/components/dropdown/dropdown-item.component'
import { SvgIconComponent } from '../svg-icon/svg-icon.component'

@Component({
    selector: 'app-pagination',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
    imports: [ZardDropdownMenuComponent, ZardDropdownMenuItemComponent, SvgIconComponent],
})
export class PaginationComponent {
    @Input() pagination: IPagination | null = null
    @Input() enhancedPagination: IEnhancedPagination | null = null
    @Output() pageChange = new EventEmitter<number>()
    @Output() pageSizeChange = new EventEmitter<number>()
    Math = Math

    get currentPage(): number {
        return this.enhancedPagination?.currentPage || this.pagination?.page || 1
    }

    get totalPages(): number {
        return this.enhancedPagination?.totalPages || this.pagination?.totalPages || 1
    }

    get totalItems(): number {
        return this.enhancedPagination?.totalItems || this.pagination?.total || 0
    }

    get pageSize(): number {
        return this.enhancedPagination?.pageSize || this.pagination?.limit || 10
    }

    get pageSizeOptions(): number[] {
        return this.enhancedPagination?.pageSizeOptions || [5, 10, 25, 50]
    }

    get visiblePages(): number[] {
        const pages: number[] = []
        const current = this.currentPage
        const total = this.totalPages

        if (total <= 7) {
            for (let i = 1; i <= total; i++) {
                pages.push(i)
            }
        } else {
            pages.push(1)
            const start = Math.max(2, current - 1)
            const end = Math.min(total - 1, current + 1)

            if (start > 2) {
                pages.push(-1)
            }

            for (let i = start; i <= end; i++) {
                if (i > 1 && i < total) {
                    pages.push(i)
                }
            }

            if (end < total - 1) {
                pages.push(-2)
            }

            if (total > 1) {
                pages.push(total)
            }
        }

        return pages
    }

    handlePageChange(page: number): void {
        if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
            this.pageChange.emit(page)
        }
    }

    handlePreviousPage(): void {
        if (this.currentPage > 1) {
            this.handlePageChange(this.currentPage - 1)
        }
    }

    handleNextPage(): void {
        if (this.currentPage < this.totalPages) {
            this.handlePageChange(this.currentPage + 1)
        }
    }

    selectPageSize(size: number): void {
        if (size !== this.pageSize) {
            this.pageSizeChange.emit(size)
        }
    }

    isCurrentPage(page: number): boolean {
        return page === this.currentPage
    }

    isDisabled(direction: 'prev' | 'next'): boolean {
        if (direction === 'prev') {
            return this.currentPage <= 1
        } else {
            return this.currentPage >= this.totalPages
        }
    }

    isEllipsis(page: number): boolean {
        return page === -1 || page === -2
    }
}
